import type { Color } from '@tencent/alloy-renderer';
import { IBaseFill } from '@src/slide/domain/ui-model/element-modeles/property/fill/base';
import { ITDColor, ITDColorAttr, TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { EEffectAlgn } from '@src/slide/domain/ui-model/element-modeles/property/effect/effect.interface';
import { ICenterFrame } from '@tencent/alloy-graphics';

/**
 * 属性的key
 */
export const FILL_KEY = 'fill-color';

export interface IFillProperty {
    /**
     * 获取填充的类型。当元素填充类型不同时，fillType为undefined
     */
    getFillType(): FillType | undefined;
    /**
     * 获取当前展示的填充的类型。当组合子元素继承自组合时，返回子元素继承的填充类型
     */
    getShownFillType(): FillType | undefined;
    /**
     * 获取当前显示的颜色。
     */
    getShownColor(): string | undefined;
    /**
     * 获取当前显示的颜色。
     */
    getShownTdColor(): ITDColor | undefined;

    /**
     * 获取元素当前的填充信息。其中，填充是互斥的，只返回当前的填充信息。
     */
    getCurrentFill(): IBaseFill | null;

    /**
     * 获取元素所有填充信息集合。
     */
    getFillInfo(): IBackgroundFillInfo | null | undefined;

    /**
     * 更新当前model的填充信息。
     * @param fillSet 待更新的填充数据。
     */
    updateCurrentFillForCurrentModel(fillSet: IBackgroundFillInfo): void;

    /**
     * 更新元素当前的填充信息。
     * @param fillSet 待更新的填充数据。
     * @param onlyUIChange 是否只更新UI
     */
    updateCurrentFill(fillSet: IBackgroundFillInfo, onlyUIChange?: boolean): void;

    /**
     * 刷新 多选图形|组合 合并后的效果值
     */
    refreshMergePropertyValCache(): IBackgroundFillInfo | null | undefined

    getShadeTitleFrame(): ICenterFrame | undefined

    /**
     * 获取当前元素的填充颜色
     */
    getFillColor(): ITDColor | undefined;
}

export enum FillType {
    NoFill = 'noFill',          // 无填充
    SolidFill = 'solidFill',    // 纯色填充
    GradualFill = 'gradFill',   // 渐变填充
    ImageFill = 'image',        // 图片填充
    PattFill = 'pattFill',      // 图案填充
}

/**
 * 填充数据属性集合
 */
export type IBackgroundFillInfo = {
    solidFill?: ISolidFill | null;   // 纯色填充所有属性
    gradFill?: IGradFill | null;     // 渐变填充所有属性
    image?: IImageFill | null;       // 图片填充所有属性
    pattFill?: IPatternFillStyle | null;   // 图案填充属性
    noFill?: number;                // 无填充
    grpFill?: number;                // 组合子元素是否继承组合填充属性
    color?: string;                  // 当前显示的颜色
    shadeToTitle?: 0 | 1;       // 是否为标题的阴影
};

export type ITextFillInfo = {
    solidFill?: ISolidFill | null;   // 纯色填充所有属性
    gradFill?: IGradFill | null;     // 渐变填充所有属性
    image?: IImageFill | null;       // 图片填充所有属性
    pattFill?: IPatternFillStyle | null;   // 图案填充属性
    noFill?: number;                // 无填充
    tdColor?: TDColor;              // 和纯色填充作用一样，只不过之前可能为了保证设置普通颜色和设置纯色填充逻辑一致，所以统一用这个颜色来表示
};

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface IBaseFillInfo {}

/**
 * 纯色填充所有属性
 */
export interface ISolidFill extends IBaseFillInfo  {
    color: TDColor;
}

/**
 * 图案填充属性，UIModel里使用
 */
export interface IPatternFillStyle extends IBaseFillInfo {
    preset: string;
    fgColor: {
        color: ITDColorAttr,
    };
    bgColor: {
        color: ITDColorAttr,
    };
}

/**
 * 图案填充所有属性，渲染层使用
 */
export interface IPattFill extends IBaseFillInfo {
    preset: string;
    fgColor: IColor;
    bgColor: IColor;
}

export interface IFillRect {
    b: number;
    t: number;
    l: number;
    r: number;
}

/**
 * 图片填充所有属性
 */
export interface IImageFill extends IBaseFillInfo {
    src: string;
    alpha?: number;
    rotWithShape?: 0 | 1; // false是不随形状旋转，true是随形状旋转，默认为true
    tile?: IImageTile;
    stretch?: {
        fillRect?: Partial<IFillRect>;
    };
}

/**
 * 渐变填充所有属性
 */
export interface IGradFill extends IBaseFillInfo  {
    /**
     * 渐变点数组
     */
    gsList: Array<IGradFillPoint>;
    /**
     * 线性渐变独有属性
     */
    lin?: {
        ang?: number; // 角度
        scaled: number; // 角度是否随填充区域缩放
    };
    pathGradient: {
        path: GradientType;
        /**
         * 径向渐变独有属性
         */
        fillToRect?: IFillRect;
    };
    rotateWithShape?: 1 | 0;
    tileRect?: IFillRect;
}

export type IColor = Color | null;

export interface IGradFillPoint {
    pos: number;
    color: TDColor | null;
}

export enum GradientType {
    /**
     * 线性渐变
     */
    Linear = 'linear',
    /**
     * 径向渐变
     */
    Radial = 'circle',
    /**
     * 矩形渐变
     */
    Rect = 'rect',
    /**
     * 路径渐变
     */
    Shape = 'shape',
}

export interface IImageTile {
    /**
     * 图片x轴偏移量
     */
    tx: number;
    /**
     * 图片y轴偏移量
     */
    ty: number;
    /**
     * 图片x轴百分比
     */
    sx: number;
    /**
     * 图片y轴百分比
     */
    sy: number;
    flip: ImageTileFlipType;
    algn: EEffectAlgn;
}

export enum ImageTileFlipType {
    NONE = 'none',
    xy = 'xy',
    x = 'x',
    y = 'y',
}

export enum ImageTileAlgnType {
    TL = 'tl',
    TR = 'tr',
    BL = 'bl',
    BR = 'br',
    T = 't',
    L = 'l',
    CTR = 'ctr',
    R = 'r',
    B = 'b',
}
